// library imports
import {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useContext,
  useMemo,
} from "react";
import {
  theme,
  Button,
  DatePicker,
  Flex,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Grid,
  Row,
  Col,
  Card,
  Skeleton,
} from "antd";
import {
  AlertOutlined,
  DownloadOutlined,
  SearchOutlined,
  UndoOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useForm } from "antd/es/form/Form";
// hook imports
import useFetch from "../../hooks/useFetch";
import { IndexContext } from "../../context/IndexContext";
// config import
import {
  MQTT_REFRESH_RATE,
  DARK_BLUE,
  LIGHT_BLUE,
  LIGHT_LIGHT_BLUE,
  DARK_GREEN,
  LIGHT_GREEN,
  ORANGE,
  DARK_THEME_DARK_BLUE,
  PURPLE,
} from "../../configs/configs";
// component imp
import AcknowledgeAlarmForm from "../../components/common/AcknowledgeAlarmForm";
import AlarmDetailForm from "../../components/alarm/AlarmDetailForm";
import CustomGroupedStackedBarChart from "../../components/common/CustomGroupedStackedBarChart";
import CustomBarChart from "../../components/common/CustomBarChart";
import CustomLineBarChart from "../../components/common/CustomLineBarChart";
import DataTable from "../../components/common/DataTable";
// style imports
import "../../styles/Alarm/AlarmPage.css";

const { Title, Text } = Typography;
const { useToken } = theme;

export default function AlarmTrendingPage() {
  const { token } = useToken();
  const tableWrapperViewportHeight = 1;

  const { siteSelected, notificationApi, messageApi, modalApi } =
    useContext(IndexContext);
  const [fetchWrapper] = useFetch();
  const [form] = useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedAlarmView, setSelectedAlarmView] = useState("Active");

  const [deviceList, setDeviceList] = useState([]);
  const [alarmCodeOptions, setAlarmCodeOptions] = useState([]);

  const [activeAlarmFilter, setActiveAlarmFilter] = useState([]);
  const [historicalAlarmFilter, setHistoricalAlarmFilter] = useState([]);
  const [alarmCodeHistoricalAlarmFilter, setAlarmCodeHistoricalAlarmFilter] =
    useState([]);

  // site commissioning date
  const [siteCommissioningDate, setSiteCommissioningDate] = useState(dayjs());

  const [startDate, setStartDate] = useState(
    dayjs(
      Math.max(
        dayjs().hour(0).minute(0).second(0).valueOf(),
        dayjs(siteCommissioningDate, "YYYY-MM-DD").valueOf(),
      ),
    ),
  );
  const [endDate, setEndDate] = useState(
    dayjs().hour(0).minute(0).second(0).add(1, "day"),
  );
  const [deviceSelected, setDeviceSelected] = useState([]);

  const [firstLoadActiveAlarm, setFirstLoadActiveAlarm] = useState(true);
  const [firstLoadHistoricalAlarm, setFirstLoadHistoricalAlarm] =
    useState(true);
  const [
    firstLoadAlarmCodeHistoricalAlarm,
    setFirstLoadAlarmCodeHistoricalAlarm,
  ] = useState(true);
  const [alarmCodeSelected, setAlarmCodeSelected] = useState([]);

  // dummy data for active alarm table
  // const [activeAlarmData, setActiveAlarmData] = useState(() => {
  //   // Map each duration bucket to a realistic start datetime offset (days ago)
  //   const durationStartMap = {
  //     "1 day": 1,
  //     "3 days": 3,
  //     "5 days": 5,
  //     "7 days": 7,
  //     ">7 days": 10,
  //   };
  //   const durations = ["1 day", "3 days", "5 days", "7 days", ">7 days"];
  //   const records = [];
  //   durations.forEach((duration) => {
  //     const daysAgo = durationStartMap[duration];
  //     for (let i = 0; i < 10; i++) {
  //       const startDt = dayjs()
  //         .subtract(daysAgo, "day")
  //         .hour(8 + Math.floor(i / 2))
  //         .minute((i % 2) * 30)
  //         .second(0)
  //         .format("YYYY-MM-DD HH:mm:ss");
  //       records.push({
  //         start: startDt,
  //         active_duration: duration,
  //         device_name: "BESS 1",
  //         level: "Error",
  //         alarm_code: `E0${i % 9}`,
  //         description: "BMS fault",
  //         AcknowledgeBy: "",
  //         acknowledged_datetime: "",
  //         acknowledged: false,
  //       });
  //       records.push({
  //         start: startDt,
  //         active_duration: duration,
  //         device_name: "BESS 1",
  //         level: "Warning",
  //         alarm_code: `E1${i}`,
  //         description: "High temperature",
  //         AcknowledgeBy: "",
  //         acknowledged_datetime: "",
  //         acknowledged: false,
  //       });
  //     }
  //     for (let i = 0; i < 5; i++) {
  //       const startDt = dayjs()
  //         .subtract(daysAgo, "day")
  //         .hour(8 + i)
  //         .minute(0)
  //         .second(0)
  //         .format("YYYY-MM-DD HH:mm:ss");
  //       records.push({
  //         start: startDt,
  //         active_duration: duration,
  //         device_name: "BESS 2",
  //         level: "Error",
  //         alarm_code: `E0${i}`,
  //         description: "BMS fault",
  //         AcknowledgeBy: "",
  //         acknowledged_datetime: "",
  //         acknowledged: false,
  //       });
  //       records.push({
  //         start: startDt,
  //         active_duration: duration,
  //         device_name: "BESS 2",
  //         level: "Warning",
  //         alarm_code: `E2${i}`,
  //         description: "Low SOC",
  //         AcknowledgeBy: "",
  //         acknowledged_datetime: "",
  //         acknowledged: false,
  //       });
  //     }
  //   });
  //   return records;
  // });
  // dummy data for historical alarm table
  // const [historicalAlarmData, setHistoricalAlarmData] = useState(() => {
  //   const dates = [
  //     "2026-07-17",
  //     "2026-07-18",
  //     "2026-07-19",
  //     "2026-07-20",
  //     "2026-07-21",
  //     "2026-07-22",
  //   ];
  //   const records = [];
  //   dates.forEach((date) => {
  //     for (let i = 0; i < 10; i++) {
  //       records.push({
  //         start: `${date} ${String(8 + Math.floor(i / 2)).padStart(2, "0")}:${String((i % 2) * 30).padStart(2, "0")}:00`,
  //         device_name: "BESS 1",
  //         level: "Error",
  //         end: `${date} 18:00:00`,
  //         parameter_group: "BMS",
  //         parameter: "Fault Code",
  //         value: `E0${i % 9}`,
  //         message: "BMS fault",
  //         AcknowledgeBy: "",
  //         acknowledged_datetime: "",
  //         acknowledged: false,
  //       });
  //       records.push({
  //         start: `${date} ${String(8 + Math.floor(i / 2)).padStart(2, "0")}:${String((i % 2) * 30).padStart(2, "0")}:00`,
  //         device_name: "BESS 1",
  //         level: "Warning",
  //         end: `${date} 18:00:00`,
  //         parameter_group: "Temperature",
  //         parameter: "Pack Temp",
  //         value: `E1${i % 9}`,
  //         message: "High temperature",
  //         AcknowledgeBy: "",
  //         acknowledged_datetime: "",
  //         acknowledged: false,
  //       });
  //     }
  //     for (let i = 0; i < 5; i++) {
  //       records.push({
  //         start: `${date} ${String(8 + i).padStart(2, "0")}:00:00`,
  //         device_name: "BESS 2",
  //         level: "Error",
  //         end: `${date} 18:00:00`,
  //         parameter_group: "BMS",
  //         parameter: "Fault Code",
  //         value: `E0${i}`,
  //         message: "BMS fault",
  //         AcknowledgeBy: "",
  //         acknowledged_datetime: "",
  //         acknowledged: false,
  //       });
  //       records.push({
  //         start: `${date} ${String(8 + i).padStart(2, "0")}:30:00`,
  //         device_name: "BESS 2",
  //         level: "Warning",
  //         end: `${date} 18:00:00`,
  //         parameter_group: "SOC",
  //         parameter: "SOC",
  //         value: `E2${i}`,
  //         message: "Low SOC",
  //         AcknowledgeBy: "",
  //         acknowledged_datetime: "",
  //         acknowledged: false,
  //       });
  //     }
  //   });
  //   return records;
  // });

  const [activeAlarmData, setActiveAlarmData] = useState([]);
  const [historicalAlarmData, setHistoricalAlarmData] = useState([]);
  const [alarmCodeHistoricalData, setAlarmCodeHistoricalData] = useState([]);

  // bar click drill-down
  const [barDrillDown, setBarDrillDown] = useState({
    open: false,
    data: [],
    title: "",
  });

  const ACTIVE_ALARM_DURATION_ORDER = [
    "1 day",
    "3 days",
    "5 days",
    "7 days",
    ">7 days",
  ];

  function handleBarClick(datum) {
    const source =
      selectedAlarmView === "Active"
        ? activeAlarmData
        : selectedAlarmView === "Historical"
          ? historicalAlarmData
          : alarmCodeHistoricalData;
    const filtered = source.filter((alarm) => {
      const matchDate =
        selectedAlarmView === "Active"
          ? alarm.active_duration === datum.date
          : selectedAlarmView === "Historical (by Alarm Code)"
            ? alarm.description === datum.date
            : dayjs(alarm.start).format("YYYY-MM-DD") === datum.date;
      const matchDevice = alarm.device_name === datum.device;
      const matchLevel = datum.level
        ? alarm.level?.toLowerCase() === datum.level?.toLowerCase()
        : true;
      return matchDate && matchDevice && matchLevel;
    });
    setBarDrillDown({
      open: true,
      data: filtered,
      title: datum.level
        ? `${datum.date}  ·  ${datum.device}  ·  ${datum.level}`
        : `${datum.date}  ·  ${datum.device}`,
    });
  }

  // for resizeable table
  const [tableHeight, setTableHeight] = useState(600);
  const filterRef = useRef();

  function resizeTable() {
    const node = filterRef.current;
    if (!node) {
      return;
    }
    const { height } = node.getBoundingClientRect();

    setTableHeight(
      window.innerHeight * tableWrapperViewportHeight - height - 285,
    );
  }

  /* API to get site commissioning date */
  function getSiteCommissioningDate() {
    fetchWrapper({
      endpoint_url: "common/getSiteCommissioningDate",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },

      onSuccess: (response) => {
        setSiteCommissioningDate(response.data.site_commissioning_date);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get site commissioning date.",
          description: response.statusText,
        });
      },
    });
  }

  /* API to get device list*/
  function getDeviceList() {
    fetchWrapper({
      endpoint_url: "alarm/getDeviceList",
      method: "GET",
      set_content_type_header: false,
      params: { site_id: siteSelected },
      onSuccess: (response) => {
        setDeviceList(response.data);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get device list.",
          description: response.statusText,
        });
        setDeviceList([]);
      },
    });
  }

  /* API to get alarm code list*/
  function getAlarmCodeList() {
    fetchWrapper({
      endpoint_url: "alarm_trending/getAlarmCodeList",
      method: "GET",
      set_content_type_header: false,
      params: { site_id: siteSelected },
      onSuccess: (response) => {
        setAlarmCodeOptions(response.data);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get alarm code list.",
          description: response.statusText,
        });
        setAlarmCodeOptions([]);
      },
    });
  }

  /* API to get active alarm */
  function getSelectedSiteActiveAlarm() {
    fetchWrapper({
      endpoint_url: "alarm_trending/getActiveAlarm",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        device_id_list:
          activeAlarmFilter.length === 0
            ? deviceList.map((item) => item.value)
            : activeAlarmFilter,
      },
      onSuccess: (response) => {
        setActiveAlarmData(response.data);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get active alarm.",
          description: response.statusText,
        });
      },
      onFinish: () => {
        setFirstLoadActiveAlarm(false);
      },
    });
  }

  /* API to get historical alarm */
  function getSelectedSiteHistoricalAlarm() {
    setFirstLoadHistoricalAlarm(true);
    fetchWrapper({
      endpoint_url: "alarm_trending/getHistoricalAlarm",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        device_id_list:
          historicalAlarmFilter.length !== 0
            ? historicalAlarmFilter.device_id_list.length === 0
              ? deviceList.map((item) => item.value)
              : historicalAlarmFilter.device_id_list
            : deviceList.map((item) => item.value),
        start_date:
          historicalAlarmFilter.length !== 0
            ? historicalAlarmFilter.start_date
            : startDate.format("YYYY-MM-DD HH:mm:ss"), // this condition is catered for the initial calling of the API where search button is not clicked yet
        end_date:
          historicalAlarmFilter.length !== 0
            ? historicalAlarmFilter.end_date
            : endDate.format("YYYY-MM-DD HH:mm:ss"),
      },
      onSuccess: (response) => {
        setHistoricalAlarmData(response.data);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get historical alarm.",
          description: response.statusText,
        });
      },
      onFinish: () => {
        setFirstLoadHistoricalAlarm(false);
      },
    });
  }

  /* API to get alarm code historical alarm */
  function getSelectedSiteAlarmCodeHistoricalAlarm() {
    setFirstLoadAlarmCodeHistoricalAlarm(true);
    fetchWrapper({
      endpoint_url: "alarm_trending/getAlarmCodeHistoricalAlarm",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        alarm_code_list:
          alarmCodeHistoricalAlarmFilter.length !== 0
            ? alarmCodeHistoricalAlarmFilter.alarm_code_list.length === 0
              ? alarmCodeOptions.map((item) => item.value)
              : alarmCodeHistoricalAlarmFilter.alarm_code_list
            : alarmCodeOptions.map((item) => item.value),
        start_date:
          alarmCodeHistoricalAlarmFilter.length !== 0
            ? alarmCodeHistoricalAlarmFilter.start_date
            : startDate.format("YYYY-MM-DD HH:mm:ss"), // this condition is catered for the initial calling of the API where search button is not clicked yet
        end_date:
          alarmCodeHistoricalAlarmFilter.length !== 0
            ? alarmCodeHistoricalAlarmFilter.end_date
            : endDate.format("YYYY-MM-DD HH:mm:ss"),
      },
      onSuccess: (response) => {
        setAlarmCodeHistoricalData(response.data);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get historical alarm.",
          description: response.statusText,
        });
      },
      onFinish: () => {
        setFirstLoadAlarmCodeHistoricalAlarm(false);
      },
    });
  }

  // function to download the file
  function downloadFile() {
    return new Promise((resolve, reject) => {
      fetchWrapper({
        endpoint_url: "alarm/downloadFile",
        method: "POST",
        json_output: false,
        body: {
          data:
            selectedAlarmView === "Active"
              ? activeAlarmData
              : historicalAlarmData,
          sheet_name:
            selectedAlarmView === "Active"
              ? "Active Alarm"
              : "Historical Alarm",
        },
        json_output: false,
        onSuccess: (response) => {
          // returned a json response.
          if (response.headers.get("content-type") === "application/json") {
            response.json().then((data) => {
              if (Object.keys(data).includes("error")) {
                // has issue with the download file
                messageApi.open({
                  type: "error",
                  content: "Failed to download file",
                });
                resolve(false);
              } else {
                // returned something else.
                messageApi.open({
                  type: "error",
                  content: "Failed to download file.",
                });
                resolve(false);
              }
            });
          } else {
            // no issue with the response. returned a spreadsheet
            response.blob().then((blob) => {
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download =
                selectedAlarmView === "Active"
                  ? `Active_Alarm_${dayjs().format("YYYY-MM-DDTHH:mm:ss")}.xlsx`
                  : `Historical_Alarm_${dayjs().format(
                      "YYYY-MM-DDTHH:mm:ss",
                    )}.xlsx`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
              resolve(true);
            });
          }
        },
        onResponseError: (err) => {
          messageApi.open({
            type: "error",
            content: "Failed to download file",
          });
          resolve(false);
        },
      });
    });
  }

  useEffect(() => {
    setBarDrillDown((s) => ({ ...s, open: false }));
    setFirstLoadActiveAlarm(true);
    setFirstLoadHistoricalAlarm(true);
    setFirstLoadAlarmCodeHistoricalAlarm(true);
    if (siteSelected) {
      getDeviceList();
      getAlarmCodeList();
      getSiteCommissioningDate();
    }
  }, [siteSelected]);

  // when site commissioning date changes, need to update start date and historical alarm filter
  useEffect(() => {
    setStartDate(
      dayjs(
        Math.max(
          dayjs().hour(0).minute(0).second(0).valueOf(),
          dayjs(siteCommissioningDate, "YYYY-MM-DD").valueOf(),
        ),
      ),
    );

    setHistoricalAlarmFilter({
      start_date: dayjs(
        Math.max(
          dayjs().hour(0).minute(0).second(0).valueOf(),
          dayjs(siteCommissioningDate, "YYYY-MM-DD").valueOf(),
        ),
      ).format("YYYY-MM-DD HH:mm:ss"),
      end_date: endDate.format("YYYY-MM-DD HH:mm:ss"),
      device_id_list: deviceSelected,
    });

    setAlarmCodeHistoricalAlarmFilter({
      start_date: dayjs(
        Math.max(
          dayjs().hour(0).minute(0).second(0).valueOf(),
          dayjs(siteCommissioningDate, "YYYY-MM-DD").valueOf(),
        ),
      ).format("YYYY-MM-DD HH:mm:ss"),
      end_date: endDate.format("YYYY-MM-DD HH:mm:ss"),
      alarm_code_list: [],
    });
  }, [siteCommissioningDate]);

  useEffect(() => {
    // call the API only when device list has been fetched
    if (deviceList.length > 0) {
      if (selectedAlarmView === "Active") {
        if (siteSelected) {
          getSelectedSiteActiveAlarm();

          // refresh data every configurable second
          const timer = setInterval(() => {
            getSelectedSiteActiveAlarm();
          }, MQTT_REFRESH_RATE * 1000);

          return () => {
            clearInterval(timer);
          };
        }
      }
    }
  }, [deviceList, siteSelected, selectedAlarmView, activeAlarmFilter]);

  useEffect(() => {
    // call the API only when device list has been fetched
    if (deviceList.length > 0) {
      if (selectedAlarmView === "Historical") {
        if (siteSelected) {
          getSelectedSiteHistoricalAlarm();
        }
      }
    }
  }, [deviceList, siteSelected, selectedAlarmView, historicalAlarmFilter]);

  useEffect(() => {
    // call the API only when device list has been fetched
    if (alarmCodeOptions.length > 0) {
      if (selectedAlarmView === "Historical (by Alarm Code)") {
        if (siteSelected) {
          getSelectedSiteAlarmCodeHistoricalAlarm();
        }
      }
    }
  }, [
    alarmCodeOptions,
    siteSelected,
    selectedAlarmView,
    alarmCodeHistoricalAlarmFilter,
  ]);

  useLayoutEffect(() => {
    resizeTable();
    window.addEventListener("resize", resizeTable);

    return () => {
      window.removeEventListener("resize", resizeTable);
    };
  }, [filterRef]);

  useEffect(() => {
    resizeTable();
  }, [selectedAlarmView]);

  const chartData = useMemo(() => {
    const source =
      selectedAlarmView === "Active"
        ? activeAlarmData
        : selectedAlarmView === "Historical"
          ? historicalAlarmData
          : alarmCodeHistoricalData;
    const counts = {};
    source.forEach((alarm) => {
      const date =
        selectedAlarmView === "Active"
          ? alarm.active_duration
          : dayjs(alarm.start).format("YYYY-MM-DD");
      const device = alarm.device_name || "Unknown";
      const level = (alarm.level || "").toLowerCase();
      const key = `${date}|||${device}`;
      if (!counts[key]) counts[key] = { date, device, error: 0, warning: 0 };
      if (level === "error") counts[key].error += 1;
      else if (level === "warning") counts[key].warning += 1;
    });
    const rows = Object.values(counts);
    if (selectedAlarmView === "Active") {
      rows.sort(
        (a, b) =>
          ACTIVE_ALARM_DURATION_ORDER.indexOf(a.date) -
          ACTIVE_ALARM_DURATION_ORDER.indexOf(b.date),
      );
    }
    return rows;
  }, [
    activeAlarmData,
    historicalAlarmData,
    alarmCodeHistoricalData,
    selectedAlarmView,
  ]);

  const alarmStats = useMemo(() => {
    const source =
      selectedAlarmView === "Active"
        ? activeAlarmData
        : selectedAlarmView === "Historical"
          ? historicalAlarmData
          : alarmCodeHistoricalData;
    return {
      total: source.length,
      error: source.filter((a) => a.level?.toUpperCase() === "ERROR").length,
      warning: source.filter((a) => a.level?.toUpperCase() === "WARNING")
        .length,
    };
  }, [
    activeAlarmData,
    historicalAlarmData,
    alarmCodeHistoricalData,
    selectedAlarmView,
  ]);

  const alarmCodeStats = useMemo(() => {
    const counts = {};
    alarmCodeHistoricalData.forEach((alarm) => {
      const code = alarm.description || "Unknown";
      if (!counts[code])
        counts[code] = { count: 0, hasError: false, hasWarning: false };
      counts[code].count += 1;
      if (alarm.level?.toUpperCase() === "ERROR") counts[code].hasError = true;
      if (alarm.level?.toUpperCase() === "WARNING")
        counts[code].hasWarning = true;
    });
    return Object.entries(counts)
      .map(([code, { count, hasError, hasWarning }]) => ({
        code,
        count,
        hasError,
        hasWarning,
      }))
      .sort((a, b) => b.count - a.count);
  }, [alarmCodeHistoricalData]);

  const alarmCodeChartData = useMemo(() => {
    const counts = {};
    alarmCodeHistoricalData.forEach((alarm) => {
      const code = alarm.description || "Unknown";
      const device = alarm.device_name || "Unknown";
      const key = `${code}|||${device}`;
      if (!counts[key]) counts[key] = { date: code, device, count: 0 };
      counts[key].count += 1;
    });
    return Object.values(counts);
  }, [alarmCodeHistoricalData]);

  useEffect(() => {
    setActiveAlarmFilter([]);
    setHistoricalAlarmFilter([]);
    setAlarmCodeHistoricalAlarmFilter([]);
    setDeviceSelected([]);
    setAlarmCodeSelected([]);
  }, [selectedAlarmView]);

  return (
    <div
      style={{
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <Flex justify="space-between" align="center" className="mb-2">
        <Title level={5} className="m-0">
          {selectedAlarmView === "Historical (by Alarm Code)"
            ? "Historical Alarm (by Device Alarm Code)"
            : `${selectedAlarmView} Alarm`}
        </Title>
        <Segmented
          options={["Active", "Historical", "Historical (by Alarm Code)"]}
          defaultValue={selectedAlarmView}
          onChange={(value) => {
            setSelectedAlarmView(value);
            setFirstLoadActiveAlarm(true);
            setFirstLoadHistoricalAlarm(true);
            setFirstLoadAlarmCodeHistoricalAlarm(true);
            setBarDrillDown({ open: false, data: [], title: "" });
          }}
        ></Segmented>
      </Flex>

      <div ref={filterRef}>
        {selectedAlarmView === "Historical" ? (
          <Flex
            className="mb-4"
            gap={8}
            wrap={true}
            align="flex-start"
            justify="space-between"
          >
            <Space.Compact style={{ minWidth: 200, flex: 1 }}>
              <Button disabled>Device</Button>
              <Select
                placeholder={
                  <Tag color={token.colorPrimary} className="pl-3 pr-3">
                    <Text style={{ color: "white" }}>All</Text>
                  </Tag>
                }
                style={{ minWidth: 150, flex: 1 }}
                value={deviceSelected}
                options={deviceList}
                mode={"multiple"}
                allowClear
                tagRender={({ label, onClose, closable }) => (
                  <Tag
                    color={token.colorPrimary}
                    closable={closable}
                    onClose={onClose}
                    style={{
                      marginRight: 3,
                    }}
                  >
                    <Text style={{ color: "white" }}>{label}</Text>
                  </Tag>
                )}
                filterOption={(input, option) =>
                  String(option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={(value) => {
                  setDeviceSelected(value);
                }}
              />
            </Space.Compact>

            <Space.Compact block style={{ width: "fit-content" }}>
              <Button disabled>Start</Button>
              <DatePicker
                showTime
                showHour
                showMinute
                showSecond={false}
                allowClear={false}
                value={startDate}
                placement="bottomLeft"
                defaultValue={startDate}
                needConfirm={false}
                onChange={(date) => {
                  if (date > endDate) {
                    setStartDate(endDate);
                    setEndDate(date);
                  } else {
                    setStartDate(date);
                  }
                }}
                maxDate={endDate}
                minDate={dayjs(siteCommissioningDate, "YYYY-MM-DD")}
              />
              <Button disabled>End</Button>
              <DatePicker
                showTime
                showHour
                showMinute
                showSecond={false}
                allowClear={false}
                value={endDate}
                defaultValue={endDate}
                needConfirm={false}
                onChange={(date) => {
                  if (date < startDate) {
                    setEndDate(startDate);
                    setStartDate(date);
                  } else {
                    setEndDate(date);
                  }
                }}
                minDate={startDate}
                placement="topLeft"
              />
            </Space.Compact>

            <Flex gap={8}>
              <Tooltip title="Reset to default setting">
                <Button
                  icon={<UndoOutlined />}
                  onClick={() => {
                    setStartDate(
                      dayjs(
                        Math.max(
                          dayjs()
                            .hour(0)
                            .minute(0)
                            .second(0)
                            .add(-14, "day")
                            .valueOf(),
                          dayjs(siteCommissioningDate, "YYYY-MM-DD").valueOf(),
                        ),
                      ),
                    );
                    setEndDate(
                      dayjs().hour(0).minute(0).second(0).add(1, "day"),
                    );
                    setDeviceSelected([]);
                    setHistoricalAlarmFilter({
                      start_date: dayjs()
                        .hour(0)
                        .minute(0)
                        .second(0)
                        .add(-14, "day")
                        .format("YYYY-MM-DD HH:mm:ss"),
                      end_date: dayjs()
                        .hour(0)
                        .minute(0)
                        .second(0)
                        .add(1, "day")
                        .format("YYYY-MM-DD HH:mm:ss"),
                      device_id_list: [],
                    });
                  }}
                />
              </Tooltip>
              <Tooltip title="Search">
                <Button
                  icon={<SearchOutlined />}
                  onClick={() => {
                    setHistoricalAlarmFilter({
                      start_date: startDate.format("YYYY-MM-DD HH:mm:ss"),
                      end_date: endDate.format("YYYY-MM-DD HH:mm:ss"),
                      device_id_list: deviceSelected,
                    });
                    setBarDrillDown((s) => ({ ...s, open: false }));
                  }}
                />
              </Tooltip>

              <Tooltip
                title={
                  historicalAlarmData.length === 0 ? "No data to download" : ""
                }
              >
                <Button
                  disabled={historicalAlarmData.length === 0}
                  icon={<DownloadOutlined />}
                  onClick={downloadFile}
                  disabled={true}
                />
              </Tooltip>
            </Flex>
          </Flex>
        ) : selectedAlarmView === "Historical (by Alarm Code)" ? (
          <Flex
            className="mb-4"
            gap={8}
            wrap={true}
            align="flex-start"
            justify="space-between"
          >
            <Space.Compact style={{ minWidth: 200, flex: 1 }}>
              <Button disabled>Alarm Code</Button>
              <Select
                placeholder={
                  <Tag color={token.colorPrimary} className="pl-3 pr-3">
                    <Text style={{ color: "white" }}>All</Text>
                  </Tag>
                }
                style={{ minWidth: 150, flex: 1 }}
                value={alarmCodeSelected}
                options={alarmCodeOptions}
                mode={"multiple"}
                allowClear
                tagRender={({ label, onClose, closable }) => (
                  <Tag
                    color={token.colorPrimary}
                    closable={closable}
                    onClose={onClose}
                    style={{ marginRight: 3 }}
                  >
                    <Text style={{ color: "white" }}>{label}</Text>
                  </Tag>
                )}
                filterOption={(input, option) =>
                  String(option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={(value) => setAlarmCodeSelected(value)}
              />
            </Space.Compact>

            <Space.Compact block style={{ width: "fit-content" }}>
              <Button disabled>Start</Button>
              <DatePicker
                showTime
                showHour
                showMinute
                showSecond={false}
                allowClear={false}
                value={startDate}
                placement="bottomLeft"
                defaultValue={startDate}
                needConfirm={false}
                onChange={(date) => {
                  if (date > endDate) {
                    setStartDate(endDate);
                    setEndDate(date);
                  } else {
                    setStartDate(date);
                  }
                }}
                maxDate={endDate}
                minDate={dayjs(siteCommissioningDate, "YYYY-MM-DD")}
              />
              <Button disabled>End</Button>
              <DatePicker
                showTime
                showHour
                showMinute
                showSecond={false}
                allowClear={false}
                value={endDate}
                defaultValue={endDate}
                needConfirm={false}
                onChange={(date) => {
                  if (date < startDate) {
                    setEndDate(startDate);
                    setStartDate(date);
                  } else {
                    setEndDate(date);
                  }
                }}
                minDate={startDate}
                placement="topLeft"
              />
            </Space.Compact>

            <Flex gap={8}>
              <Tooltip title="Reset to default setting">
                <Button
                  icon={<UndoOutlined />}
                  onClick={() => {
                    setStartDate(
                      dayjs(
                        Math.max(
                          dayjs()
                            .hour(0)
                            .minute(0)
                            .second(0)
                            .add(-14, "day")
                            .valueOf(),
                          dayjs(siteCommissioningDate, "YYYY-MM-DD").valueOf(),
                        ),
                      ),
                    );
                    setEndDate(
                      dayjs().hour(0).minute(0).second(0).add(1, "day"),
                    );
                    setAlarmCodeSelected([]);
                    setAlarmCodeHistoricalAlarmFilter({
                      start_date: dayjs()
                        .hour(0)
                        .minute(0)
                        .second(0)
                        .add(-14, "day")
                        .format("YYYY-MM-DD HH:mm:ss"),
                      end_date: dayjs()
                        .hour(0)
                        .minute(0)
                        .second(0)
                        .add(1, "day")
                        .format("YYYY-MM-DD HH:mm:ss"),
                      alarm_code_list: [],
                    });
                  }}
                />
              </Tooltip>
              <Tooltip title="Search">
                <Button
                  icon={<SearchOutlined />}
                  onClick={() => {
                    setAlarmCodeHistoricalAlarmFilter({
                      start_date: startDate.format("YYYY-MM-DD HH:mm:ss"),
                      end_date: endDate.format("YYYY-MM-DD HH:mm:ss"),
                      alarm_code_list: alarmCodeSelected,
                    });
                    setBarDrillDown((s) => ({ ...s, open: false }));
                  }}
                />
              </Tooltip>
              <Tooltip
                title={
                  historicalAlarmData.length === 0 ? "No data to download" : ""
                }
              >
                <Button
                  disabled={historicalAlarmData.length === 0}
                  icon={<DownloadOutlined />}
                  onClick={downloadFile}
                  disabled={true}
                />
              </Tooltip>
            </Flex>
          </Flex>
        ) : (
          <Flex
            className="mb-4"
            gap={8}
            wrap={true}
            align="flex-start"
            justify="space-between"
          >
            <Space.Compact style={{ minWidth: 200, flex: 1 }}>
              <Button disabled>Device</Button>
              <Select
                placeholder={
                  <Tag color={token.colorPrimary} className="pl-3 pr-3">
                    <Text style={{ color: "white" }}>All</Text>
                  </Tag>
                }
                style={{ minWidth: 150, flex: 1 }}
                value={deviceSelected}
                options={deviceList}
                mode={"multiple"}
                allowClear
                tagRender={({ label, onClose, closable }) => (
                  <Tag
                    color={token.colorPrimary}
                    closable={closable}
                    onClose={onClose}
                    style={{
                      marginRight: 3,
                    }}
                  >
                    <Text style={{ color: "white" }}>{label}</Text>
                  </Tag>
                )}
                filterOption={(input, option) =>
                  String(option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={(value) => {
                  setDeviceSelected(value);
                }}
              />
            </Space.Compact>

            <Flex gap={8}>
              <Tooltip title="Search">
                <Button
                  icon={<SearchOutlined />}
                  onClick={() => {
                    setActiveAlarmFilter(deviceSelected);
                    setBarDrillDown((s) => ({ ...s, open: false }));
                    setFirstLoadActiveAlarm(true);
                  }}
                />
              </Tooltip>

              <Tooltip
                title={
                  activeAlarmData.length === 0 ? "No data to download" : ""
                }
              >
                <Button
                  disabled={activeAlarmData.length === 0}
                  icon={<DownloadOutlined />}
                  onClick={downloadFile}
                  disabled={true}
                />
              </Tooltip>
            </Flex>
          </Flex>
        )}
      </div>

      {selectedAlarmView === "Historical (by Alarm Code)" ? (
        firstLoadAlarmCodeHistoricalAlarm === false ? (
          <Row gutter={[12, 12]} className="mb-3">
            {alarmCodeStats.map(({ code, count, hasError, hasWarning }) => (
              <Col key={code} xs={12} sm={8} md={6} lg={4} xl={4} xxl={4}>
                <Card
                  title={
                    <span>
                      {hasError && (
                        <AlertOutlined
                          style={{ color: token.colorError, marginRight: 4 }}
                        />
                      )}
                      {hasWarning && (
                        <WarningOutlined
                          style={{ color: token.colorWarning, marginRight: 4 }}
                        />
                      )}
                      {code}
                    </span>
                  }
                >
                  {count}
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <Skeleton active />
        )
      ) : (
        <Row gutter={[12, 12]} className="mb-3">
          <Col xs={24} sm={24} md={24} lg={8} xl={8} xxl={8}>
            <Card title={"Total"}>
              {(selectedAlarmView === "Active" && firstLoadActiveAlarm) ||
              (selectedAlarmView === "Historical" &&
                firstLoadHistoricalAlarm) ? (
                <Skeleton active paragraph={{ rows: 1 }} title={false} />
              ) : (
                alarmStats.total
              )}
            </Card>
          </Col>

          <Col xs={24} sm={24} md={24} lg={8} xl={8} xxl={8}>
            <Card
              title={<Text style={{ color: token.colorError }}>Error</Text>}
            >
              {(selectedAlarmView === "Active" && firstLoadActiveAlarm) ||
              (selectedAlarmView === "Historical" &&
                firstLoadHistoricalAlarm) ? (
                <Skeleton active paragraph={{ rows: 1 }} title={false} />
              ) : (
                <Text style={{ color: token.colorError }}>
                  {alarmStats.error}
                </Text>
              )}
            </Card>
          </Col>

          <Col xs={24} sm={24} md={24} lg={8} xl={8} xxl={8}>
            <Card
              title={<Text style={{ color: token.colorWarning }}>Warning</Text>}
            >
              {(selectedAlarmView === "Active" && firstLoadActiveAlarm) ||
              (selectedAlarmView === "Historical" &&
                firstLoadHistoricalAlarm) ? (
                <Skeleton active paragraph={{ rows: 1 }} title={false} />
              ) : (
                <Text style={{ color: token.colorWarning }}>
                  {alarmStats.warning}
                </Text>
              )}
            </Card>
          </Col>
        </Row>
      )}

      {selectedAlarmView === "Active" &&
        (firstLoadActiveAlarm === false ? (
          <CustomGroupedStackedBarChart
            data={chartData}
            xAxisTitle="Date"
            yAxisTitle="Number of Alarms"
            onBarClick={handleBarClick}
          />
        ) : (
          <Skeleton active />
        ))}

      {selectedAlarmView === "Historical" &&
        (firstLoadHistoricalAlarm === false ? (
          <CustomGroupedStackedBarChart
            data={chartData}
            xAxisTitle="Date"
            yAxisTitle="Number of Alarms"
            onBarClick={handleBarClick}
          />
        ) : (
          <Skeleton active />
        ))}

      {selectedAlarmView === "Historical (by Alarm Code)" &&
        (firstLoadAlarmCodeHistoricalAlarm === false ? (
          <CustomGroupedStackedBarChart
            data={alarmCodeChartData}
            colorField="device"
            yField="count"
            grouped={true}
            xAxisTitle="Alarm"
            yAxisTitle="Count"
            onBarClick={handleBarClick}
          />
        ) : (
          <Skeleton active />
        ))}

      {barDrillDown.open && (
        <div className="mt-3">
          <Flex justify="space-between" align="center" className="mb-2">
            <Text strong>{barDrillDown.title}</Text>
            <Button
              size="small"
              onClick={() => setBarDrillDown((s) => ({ ...s, open: false }))}
            >
              Clear
            </Button>
          </Flex>
          <DataTable
            table_column={[
              {
                title: "Start",
                dataIndex: "start",
                ellipsis: true,
                sorter: (a, b) => {
                  // Convert date strings to Date objects
                  const dateA = new Date(a.start);
                  const dateB = new Date(b.start);
                  return dateA - dateB;
                },
              },
              ...(selectedAlarmView === "Historical" ||
              selectedAlarmView === "Historical (by Alarm Code)"
                ? [
                    {
                      title: "End",
                      dataIndex: "end",
                      ellipsis: true,
                      sorter: (a, b) => {
                        // Convert date strings to Date objects
                        const dateA = new Date(a.start);
                        const dateB = new Date(b.start);
                        return dateA - dateB;
                      },
                    },
                  ]
                : []),
              {
                title: "Device",
                dataIndex: "device_name",
                filters: [
                  ...new Set(
                    barDrillDown.data.map((r) => r.device_name).filter(Boolean),
                  ),
                ].map((v) => ({ text: v, value: v })),
                filterSearch: true,
                onFilter: (value, record) => record.device_name === value,
              },
              {
                title: "Alarm Code",
                dataIndex: "alarm_code",
                filters: [
                  ...new Set(
                    barDrillDown.data.map((r) => r.alarm_code).filter(Boolean),
                  ),
                ].map((v) => ({ text: v, value: v })),
                filterSearch: true,
                onFilter: (value, record) => record.alarm_code === value,
              },
              {
                title: "Description",
                dataIndex: "description",
                filters: [
                  ...new Set(
                    barDrillDown.data.map((r) => r.description).filter(Boolean),
                  ),
                ].map((v) => ({ text: v, value: v })),
                filterSearch: true,
                onFilter: (value, record) => record.description === value,
              },
              {
                title: "Level",
                dataIndex: "level",
                filters: [
                  {
                    text: "Warning",
                    value: "Warning",
                  },
                  {
                    text: "Error",
                    value: "Error",
                  },
                ],
                onFilter: (value, record) =>
                  record?.level?.toUpperCase().indexOf(value?.toUpperCase()) ===
                  0,
                render: (_) => (
                  <Tag
                    color={
                      _?.toUpperCase() === "ERROR"
                        ? token.colorError
                        : token.colorWarning
                    }
                  >
                    <Text style={{ color: "white" }}>{_}</Text>
                  </Tag>
                ),
              },
            ]}
            table_data={barDrillDown.data}
          />
        </div>
      )}
    </div>
  );
}
